/* @flow */

// Wires up to the MyER "Brand / Trading Account bulk import" endpoints:
//   POST /accounts/import/preview          - queues a dry-run preview job, no data is persisted
//   GET  /accounts/import/preview/{id}     - polls the preview job status/results
//   POST /accounts/import                  - queues the import for background processing
//   GET  /accounts/import/{id}             - polls the import job status/results
import { apiService } from 'services/ServiceRegistry';

export type MultipleBrandUploadPayload = {|
  brandsFile: ?File,
  tradingAccountsFile: ?File,
|};

export type ImportRowError = {|
  row: number,
  field: string,
  value: mixed,
  message: string,
|};

export type ImportPreviewRowAction = 'created' | 'updated' | 'unchanged' | 'rejected';

export type ImportRowChange = {|
  from: mixed,
  to: mixed,
|};

export type ImportCreatedWithoutAccount = {|
  id: string,
  name: string,
  account_count?: number,
|};

export type ImportPreviewRow = {|
  row: number,
  action: ImportPreviewRowAction,
  brand_id?: string,
  account_id?: string,
  account_no?: string,
  er_company_code?: string,
  changes?: { [string]: ImportRowChange },
  warnings?: ImportRowError[],
  errors?: ImportRowError[],
|};

export type ImportSectionPreview = {|
  file_name: string,
  total_rows: number,
  created_records: number,
  updated_records: number,
  unchanged_records: number,
  rejected_records: number,
  warning_records: number,
  warnings: ImportRowError[],
  errors: ImportRowError[],
  rows: ImportPreviewRow[],
|};

export type MultipleBrandPreviewResult = {|
  brands: ?ImportSectionPreview,
  tradingAccounts: ?ImportSectionPreview,
|};

export type ImportSectionResult = {|
  file_name: ?string,
  total_rows: ?number,
  created_records: ?number,
  updated_records: ?number,
  unchanged_records: ?number,
  rejected_records: ?number,
  warning_records?: ?number,
  errors: ImportRowError[],
  created_without_accounts?: ImportCreatedWithoutAccount[],
|};

export type MultipleBrandJobStatusValue =
  | 'queued'
  | 'processing'
  | 'completed'
  | 'completed_with_errors'
  | 'failed';

export type MultipleBrandJobStatus = {|
  status: MultipleBrandJobStatusValue,
  currentStage: ?string,
  failureMessage: ?string,
  brands: ?ImportSectionResult,
  tradingAccounts: ?ImportSectionResult,
|};

export type MultipleBrandPreviewJobStatus = {|
  status: MultipleBrandJobStatusValue,
  failureMessage: ?string,
  brands: ?ImportSectionPreview,
  tradingAccounts: ?ImportSectionPreview,
|};

const FINISHED_STATUSES = ['completed', 'completed_with_errors', 'failed'];

export const isImportFinished = (status: MultipleBrandJobStatusValue): boolean =>
  FINISHED_STATUSES.includes(status);

const buildFormData = (payload: MultipleBrandUploadPayload): FormData => {
  const formData = new FormData();

  if (payload.brandsFile) {
    formData.append('brands_file', payload.brandsFile);
  }

  if (payload.tradingAccountsFile) {
    formData.append('accounts_file', payload.tradingAccountsFile);
  }

  return formData;
};

export function previewMultipleBrandsUpload(
  payload: MultipleBrandUploadPayload
): Promise<{ previewId: string }> {
  return apiService
    .createFileResource('accounts/import/preview', buildFormData(payload), new Headers())
    .then(data => ({ previewId: data.preview_id }));
}

const normalizeJobStatusResponse = (data: Object) => ({
  status: data.status,
  failureMessage: data.failure_message,
  brands: data.brands || null,
  tradingAccounts: data.accounts || null,
});

export function getMultipleBrandsPreviewStatus(
  previewId: string
): Promise<MultipleBrandPreviewJobStatus> {
  return apiService
    .getResource(`accounts/import/preview/${previewId}`)
    .then(normalizeJobStatusResponse);
}

export function confirmMultipleBrandsUpload(
  payload: MultipleBrandUploadPayload
): Promise<{ jobId: string }> {
  return apiService
    .createFileResource('accounts/import', buildFormData(payload), new Headers())
    .then(data => ({ jobId: data.import_id }));
}

export function getMultipleBrandsUploadStatus(jobId: string): Promise<MultipleBrandJobStatus> {
  return apiService.getResource(`accounts/import/${jobId}`).then(data => ({
    ...normalizeJobStatusResponse(data),
    currentStage: data.current_stage,
  }));
}
